import type { StockTakeRound } from '@/infrastructure/FulfillmentAdminClient';
import { isOversold, isUnassignedShelf } from './inventoryView';
import type { InventoryScreenRow } from './inventoryView';

/**
 * A Random check is a stock audit whose shelves were drawn rather than filtered (SSD-7583). The server stores any
 * non-blank audit id, so the walk is marked by the id it opens under, and Past Audit Logs reads the mark back.
 */
export const RANDOM_CHECK_PREFIX = 'random-';

/** How many shelves a Random check draws when the box is left as it is: the center's daily ten. */
export const RANDOM_CHECK_DEFAULT = 10;

/** One draw stays a sample; past this the desk wants Start Stock Audit. */
export const RANDOM_CHECK_MAX = 100;

/** A shelf confirmed by an audit within this many days is drawn only when there are not enough others. */
export const RECENTLY_CHECKED_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

export const isRandomCheck = (auditGroupId: string | null | undefined): boolean =>
  typeof auditGroupId === 'string' && auditGroupId.startsWith(RANDOM_CHECK_PREFIX);

export const randomCheckGroupId = (uuid: string): string => `${RANDOM_CHECK_PREFIX}${uuid}`;

/** The number typed into the Random check box, or null when it is not a whole number from 1 to the maximum. */
export const randomCheckCount = (text: string): number | null => {
  const value = text.trim();
  if (!/^\d+$/.test(value)) {
    return null;
  }
  const count = Number(value);
  return count >= 1 && count <= RANDOM_CHECK_MAX ? count : null;
};

/** The start of the window a shelf counts as recently checked in, `now` minus the days. */
export const recentCutoff = (now: number, days: number = RECENTLY_CHECKED_DAYS): number => now - days * DAY_MS;

/**
 * When each shelf was last confirmed by an audit, from rounds newest first. A ✎ edit is not a count of the shelf and an
 * abandoned or open round confirmed nothing, so only confirmed audit rounds inside the window count.
 */
export const lastCheckedByShelf = (rounds: StockTakeRound[], cutoff: number): Map<string, number> => {
  const checked = new Map<string, number>();
  rounds.forEach((round) => {
    if (round.status !== 'CONFIRMED' || round.origin === 'MANUAL_ADJUST' || !round.confirmedAt) {
      return;
    }
    const at = Date.parse(round.confirmedAt);
    if (Number.isNaN(at) || at < cutoff) {
      return;
    }
    if ((checked.get(round.locationCode) ?? 0) < at) {
      checked.set(round.locationCode, at);
    }
  });
  return checked;
};

/** How long before the window a round may have started and still have been confirmed inside it. */
const OPEN_ROUND_SLACK_DAYS = 7;

/**
 * Whether a page of rounds newest first has gone past the window, so older pages need not be read. The list runs in
 * start order and a round is confirmed after it starts, so the read goes a week further back for a round left open.
 */
export const pagePassedWindow = (rounds: StockTakeRound[], cutoff: number): boolean => {
  const last = rounds[rounds.length - 1];
  const started = last?.startedAt ? Date.parse(last.startedAt) : Number.NaN;
  return !Number.isNaN(started) && started < cutoff - OPEN_ROUND_SLACK_DAYS * DAY_MS;
};

/**
 * Locations that hold stock but are not shelves the floor walks to. PACKING is the packing desk: View Orders books a
 * JIT arrival there while its order has no Shelf (viewOrders PACKING_LOCATION, SSD-7226 D3), so what sits there is on
 * its way out rather than on a shelf to count.
 */
export const NOT_DRAWN_LOCATIONS: ReadonlySet<string> = new Set(['PACKING']);

/** The shelves a draw can take: real shelves holding a countable row, each once. */
export const drawableShelves = (rows: InventoryScreenRow[], exclude: ReadonlySet<string>): string[] => {
  const shelves = new Set<string>();
  rows.forEach((row) => {
    if (
      !isUnassignedShelf(row.locationCode) &&
      !NOT_DRAWN_LOCATIONS.has(row.locationCode) &&
      !isOversold(row) &&
      !exclude.has(row.locationCode)
    ) {
      shelves.add(row.locationCode);
    }
  });
  return [...shelves];
};

const shuffled = <T>(items: T[], random: () => number): T[] => {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const other = Math.floor(random() * (index + 1));
    [result[index], result[other]] = [result[other] as T, result[index] as T];
  }
  return result;
};

/**
 * The order a Random check tries shelves in: shelves not checked in the window first, shuffled, then the recently checked
 * ones, the longest ago first. The desk opens down this list until it has its count, skipping a shelf someone holds.
 */
export const randomShelfOrder = (
  shelves: string[],
  lastChecked: ReadonlyMap<string, number>,
  random: () => number = Math.random,
): string[] => {
  const fresh = shelves.filter((shelf) => !lastChecked.has(shelf));
  const recent = shelves
    .filter((shelf) => lastChecked.has(shelf))
    .sort((a, b) => (lastChecked.get(a) ?? 0) - (lastChecked.get(b) ?? 0));
  return [...shuffled(fresh, random), ...recent];
};
